import { lazy, Suspense, MouseEvent } from 'react';
import { DSTApiResponseBundleDomain } from 'presentation/utils/dstApiResponseTypes';
import { useBrandLogos } from '@ux/whitelabel';
import { PricingStrategy } from 'presentation/components/common/Price';

import { BundleOptions, BundleText } from './available/bundle/Bundle';
import { AvailabilityStatus, DomainStatus } from './utils';
import { getClassNames } from './DomainSearchHero.stylesheet';

const AvailableDomain = lazy(() => import('./available/AvailableDomain'));
const TakenDomain = lazy(() => import('./taken/TakenDomain'));
const VoidDomain = lazy(() => import('./void/VoidDomain'));

export interface HeroCTA {
  label: string;
  analytics: string;
  glyph: string;
}

export interface PremiumText {
  enabled: boolean;
  badgeLabel: string;
  caveat: string;
}
export interface CheckoutText {
  CTA: {
    label: string;
    glyph: string;
    analytics?: {
      match: string;
      bundle: string;
      transfer: string;
    };
  };
  glyph: {
    verificationState: string;
    unavailableState: string;
    addedState: string;
  };
  label: {
    addedState: string;
    unavailableState: string;
  };
}
export interface DomainSearchHeroText {
  domain: {
    available: {
      availableState: string;
      unavailableState: string;
      addedState: string;
    };
    unavailable: {
      alreadyRegistered: string;
      notOnOffer: string;
    };
    sale: {
      badge: string;
      savings: string;
    };
    price: string;
    oldPrice: string;
    recurringPrice: string;
    CTA: HeroCTA;
  };
  bundle: BundleText;
  transfer: {
    heading: string;
    CTA: {
      label: string;
      analytics: string;
    };
  };
  checkout: CheckoutText;
  premium: {
    enabled: boolean;
    badgeLabel: string;
    caveat: string;
  };
  ad: {
    enabled: boolean;
    badgeLabel: string;
  };
}

export interface DomainSearchHeroActions {
  handleAddTransferDomainToBasket: (e?: MouseEvent<HTMLElement>) => void;
  handleAddDomainToBasket: (e?: MouseEvent<HTMLElement>) => void;
  handleAddBundleToBasket: (e?: MouseEvent<HTMLElement>) => void;
  handleViewBasket: (e?: MouseEvent<HTMLElement>) => void;
}

export interface DomainSearchHeroProps {
  availability: AvailabilityStatus;
  fqdn: string;
  fqdnExtension: string;
  price?: number;
  legalMessage?: string;
  bundleDomains?: Array<DSTApiResponseBundleDomain>;
  bundleOptions?: BundleOptions;
  isAd?: boolean;
  isPremium?: boolean;
  oldPrice?: number;
  currency: string;
  features: {
    bundle: boolean;
    dbs: boolean;
    transfer: boolean;
  };
  dbsStatus: boolean;
  canTransfer: boolean;
  status: DomainStatus;
  bundleAvailability: AvailabilityStatus;
  transferAvailability: AvailabilityStatus;
  actions: DomainSearchHeroActions;
  text: DomainSearchHeroText;
  design: HeroDesignOptions;
  pricingStrategy: PricingStrategy;
}

export interface HeroDesignOptions {
  collapsePadding: {
    top: boolean;
    bottom: boolean;
  };
}

export interface BundleDomain {
  fqdn: string;
  price: number;
  oldPrice: number;
}

const RenderHeroComponent = (props: DomainSearchHeroProps) => {
  const { logoLight } = useBrandLogos();

  switch (props.status) {
    case DomainStatus.AVAILABLE:
      return <AvailableDomain {...props} />;

    case DomainStatus.TAKEN:
      return (
        <TakenDomain
          fqdn={props.fqdn}
          features={{
            transfer: props.features.transfer,
            dbs: props.features.dbs,
          }}
          dbsStatus={props.dbsStatus}
          canTransfer={props.canTransfer}
          text={{
            transfer: props.text.transfer,
            domain: {
              alreadyRegistered: props.text.domain.unavailable.alreadyRegistered,
            },
            checkout: props.text.checkout,
          }}
          actions={{
            handleAddTransferDomainToBasket: props.actions.handleAddTransferDomainToBasket,
            handleViewBasket: props.actions.handleViewBasket,
          }}
          transferAvailability={props.transferAvailability}
          brandLogo={logoLight}
        />
      );

    case DomainStatus.VOID:
      return <VoidDomain fqdn={props.fqdn} />;

    default:
      return <AvailableDomain {...props} />;
  }
};

const DomainSearchHero = (props: DomainSearchHeroProps) => {
  const classNames = getClassNames({ design: props.design });

  return (
    <div className={classNames.root}>
      <Suspense fallback={null}>
        <RenderHeroComponent {...props} />
      </Suspense>
    </div>
  );
};

DomainSearchHero.displayName = 'DomainSearchHero';

export default DomainSearchHero;
